/**
 * Keyword search results, rendered through the shared DrugResultsTable
 * (spec 2026-09-22 "one sortable, filterable table"). Supports grouping by
 * generic name, manufacturer, application type or region, and CSV export.
 */
import React, { useState, useCallback, useMemo, useRef, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { QueryClientContext, useQuery, useQueryClient } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import { buildDrugHighlightParams } from '../utils/keywordDeepLink';
import { hasShellOpener, openInAppLink, openInNewTab } from '../utils/openInNewTab';
import { compareKeywordPath } from '../utils/searchNav';
import { kwSearchIdentity } from '../pages/keywordRequest';
import { useTab } from '../shell/TabContext';
import type { DrugSort } from '../utils/drugResultsTable';
import { drugTableRefineFields, matchesRefineChips, parseRefineParam, serializeRefineChips, type RefineChip } from '../utils/refineQuery';
import { KW_GROUP_PARAM, KW_REFINE_PARAM, parseGroupParam, serializeSortParam } from '../utils/keywordViewParams';
import {
  KW_PASSAGES_STALE_MS,
  isPassagesUnavailable,
  keywordPassagesQueryOptions,
  mergePassages,
} from '../hooks/useKeywordSearch';
import { groupRows, type GroupByOption } from '../utils/groupResults';
import type {
  KeywordSearchResultItem,
  KeywordSearchResponse,
  KeywordMatchHighlight,
  KeywordDrugBadgeSource,
  KeywordSearchRequest,
} from '../types';
import DrugResultsTable, { FilterNoMatches, ResultsFilterBox, ResultsPagination } from './DrugResultsTable';
import ExportDropdown from './ExportDropdown';
import SubscribeSearchButton from './SubscribeSearchButton';
import HighlightLegend from './HighlightLegend';
import { recolorHighlightedHtml } from './SplContentRenderer';
import { buildColoredTerms, type ColoredTerm } from '../utils/highlightColors';
import { isReservedSectionNumber } from './viewer/tocHelpers';

// ─── Sanitize headline HTML ─────────────────────────────────────────────────

function sanitizeHeadline(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['mark', 'b'],
    ALLOWED_ATTR: [],
  });
}

/** Brand-cell target: the document of the row's first match. A Near
 *  on-demand row carries no passages until expanded, so they are fetched for
 *  this one drug first; on any failure the drug-level link is kept. */
export async function keywordBrandHref(
  row: KeywordSearchResultItem,
  fetchPassages: (row: KeywordSearchResultItem) => Promise<KeywordSearchResultItem | undefined>,
  o: { keyword?: string; exactPhrase?: boolean; corpus?: string | null; tabId?: string },
): Promise<string> {
  let m = row;
  if (m.highlights.length === 0) {
    try { m = (await fetchPassages(row)) ?? row; } catch { m = row; }
  }
  return keywordDrugHref(m, m.highlights[0] ?? null, o.keyword, o.exactPhrase, o.corpus, o.tabId);
}

// Region-specific regulatory body labels
/** Body of the pre-unify ``navigateToHighlight`` — one source of truth for
 *  where a Keyword row's Brand cell (and each highlight) link to. */
export function keywordDrugHref(
  result: KeywordSearchResultItem,
  hl: KeywordMatchHighlight | null,
  searchKeywords?: string,
  exactPhrase?: boolean,
  corpus?: string | null,
  fromTabId?: string | undefined,
): string {
  const params = buildDrugHighlightParams(hl, {
    keyword: searchKeywords,
    exactPhrase,
    fallbackSection: result.highlights[0]?.section_number,
    corpus,
  });
  if (fromTabId) params.set('from', fromTabId);
  const qs = params.toString();
  return `/drug/${result.drug_id}${qs ? `?${qs}` : ''}`;
}

// ─── Source Badge ────────────────────────────────────────────────────────────

// Keyed by raw source string (Record<string,…>) so the multi-corpus sources
// the backend emits beyond the badge union — ca_pm / au_pi / au_cmi / uk_pil —
// get a proper badge instead of a blank/unstyled one. Unknown sources fall
// back to the neutral label styling.
const SOURCE_BADGE_CLASSES: Record<string, string> = {
  label: 'bg-panel text-ink-2 border-line',
  sba: 'bg-amber-50 text-amber-700 border-amber-200',
  epar: 'bg-sky-50 text-sky-700 border-sky-200',
  fda_letter: 'bg-red-50 text-red-700 border-red-200',
  fda_approval_letter: 'bg-teal-50 text-teal-700 border-teal-200',
  ca_pm: 'bg-rose-50 text-rose-700 border-rose-200',
  au_pi: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  au_cmi: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  uk_pil: 'bg-violet-50 text-violet-700 border-violet-200',
  eu: 'bg-blue-50 text-blue-700 border-blue-200',
  fda_label_pdf: 'bg-slate-50 text-slate-700 border-slate-200',
};
const SOURCE_BADGE_LABEL: Record<string, string> = {
  label: 'Label',
  sba: 'SBA',
  epar: 'EPAR',
  fda_letter: 'FDA Enforcement Letter',
  fda_approval_letter: 'FDA Approval Letter',
  ca_pm: 'CA PM',
  au_pi: 'AU PI',
  au_cmi: 'AU CMI',
  uk_pil: 'UK PIL',
  eu: 'EU SmPC',
  ie: 'IE SmPC',
  ch: 'Swiss FI',
  ch_pil: 'Swiss PI',
  de: 'DE FI',
  fr: 'FR RCP',
  ch_src: 'Swiss FI',
  it: 'IT RCP',
  fda_label_pdf: 'Drugs@FDA PI',
  // A Japanese-text hit looks like any other JP hit: the user is not told
  // it came from a separate index.
  jp_ja: 'jp',
};

/** Japanese-text snippets (jp_ja): ``lang="ja"`` + a Japanese font stack, so
 *  kanji render with Japanese glyph forms rather than a Chinese fallback. */
const JA_TEXT_STYLE: React.CSSProperties = {
  fontFamily: "'Hiragino Sans', 'Hiragino Kaku Gothic ProN', 'Yu Gothic UI', 'Yu Gothic', "
    + "Meiryo, 'Noto Sans JP', 'Noto Sans CJK JP', sans-serif",
};
const jaTextProps = (source?: string | null) =>
  source === 'jp_ja' ? { lang: 'ja', style: JA_TEXT_STYLE } : {};

const NATIVE_LANGUAGE_NAME: Record<string, string> = { de: 'German', fr: 'French', it: 'Italian' };

/** "Searched in German: …" -- the query as the server actually ran it after
 *  translating it into the label language (DE / FR native search). */
export const NativeQueryNote: React.FC<{ nq?: KeywordSearchResponse['native_query'] }> = ({ nq }) =>
  nq ? (
    <p className="text-sm text-ink-2 mb-2" data-testid="native-query-note">
      Searched in {NATIVE_LANGUAGE_NAME[nq.language] ?? nq.language}: <span className="font-medium">{nq.query}</span>
    </p>
  ) : null;

const SourceBadge: React.FC<{ source: string }> = ({ source }) => (
  <span
    className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded border ${
      SOURCE_BADGE_CLASSES[source] ?? SOURCE_BADGE_CLASSES['label']
    }`}
  >
    {SOURCE_BADGE_LABEL[source] ?? source}
  </span>
);

// ─── Keyword Matches Panel (the expanded row under a Keyword drug) ──────────

interface KeywordMatchesPanelProps {
  result: KeywordSearchResultItem;
  searchKeywords?: string;
  exactPhrase?: boolean;
  /** Per-row query terms + colors (multi-row AND/OR queries) for excerpt coloring. */
  coloredTerms?: ColoredTerm[];
}

const KeywordMatchesPanel: React.FC<KeywordMatchesPanelProps> = ({ result, searchKeywords, exactPhrase, coloredTerms }) => {
  const { tabId } = useTab();
  // Multi-row queries → re-color the backend (single-color) headline so each
  // term keeps its row color, matching the compare/export views. Single-term
  // searches keep the default amber (yellow) class — no recolor needed.
  const renderHeadline = useCallback(
    (headline: string): string => {
      const safe = sanitizeHeadline(headline);
      if (coloredTerms && coloredTerms.length > 1) {
        return recolorHighlightedHtml(
          safe,
          coloredTerms.map((c) => c.term),
          coloredTerms.map((c) => c.color),
        );
      }
      return safe;
    },
    [coloredTerms],
  );

  /** Click-through routing aware of highlight.source — see keywordDrugHref. */
  const navigateToHighlight = useCallback(
    (hl?: KeywordMatchHighlight | null) => {
      openInNewTab(keywordDrugHref(result, hl ?? null, searchKeywords, exactPhrase, undefined, tabId || undefined));
    },
    [result, searchKeywords, exactPhrase, tabId]
  );

  const sourceCounts = (['label', 'sba', 'epar', 'fda_letter'] as KeywordDrugBadgeSource[])
    .filter((src) => (result.matches_by_source?.[src] ?? 0) > 0);
  const multiSource = sourceCounts.length > 1;
  // D14: one term keeps the single `match` token; several terms keep their per-term colours.
  const markClass = coloredTerms && coloredTerms.length > 1
    ? '[&_mark]:px-0.5 [&_mark]:rounded-[2px]'
    : '[&_mark]:bg-match [&_mark]:text-ink [&_mark]:px-0.5 [&_mark]:rounded-[2px]';
  const total = result.total_matches;

  return (
    <div className="px-4 py-3">
      {total > 0 && (
        <div className="mb-2 flex items-center gap-2">
          <span data-testid="kw-matches-eyebrow" className="text-[12px] text-ink-2">
            {total} {total === 1 ? 'match' : 'matches'}
          </span>
          {multiSource && sourceCounts.map((src) => (
            <span key={src} className="px-1.5 py-0.5 text-xs font-medium rounded bg-panel text-ink-2">
              {SOURCE_BADGE_LABEL[src]} {result.matches_by_source?.[src] ?? 0}
            </span>
          ))}
        </div>
      )}

      {/* Each highlight is one button: click-through to that section. */}
      {result.highlights.map((hl, i) => (
        <button
          key={i}
          onClick={() => navigateToHighlight(hl)}
          className="grid w-full grid-cols-[180px_minmax(0,1fr)] gap-3 py-2 text-left text-[13px] leading-[1.55] hover:bg-panel cursor-pointer border-t border-line-subtle first:border-t-0"
        >
          <span className="flex flex-wrap items-start gap-1.5">
            {(multiSource || (hl.source ?? 'label') !== 'label') && <SourceBadge source={hl.source ?? 'label'} />}
            {hl.document_title != null ? (
              // Document-corpus highlight (engine letters; admin-only until
              // K4): the document is the unit, so name it instead of a section.
              <span className="font-mono text-[12px] text-accent">
                {hl.document_title}{hl.document_date ? ` · ${hl.document_date}` : ''}
              </span>
            ) : (
            <span className="font-mono text-[12px] text-accent">
              {hl.section_number && !isReservedSectionNumber(hl.section_number) ? `${hl.section_number} ` : ''}{hl.section_title}
            </span>
            )}
            {hl.section_matches != null && hl.section_matches > 0 && (
              <span className="text-[11px] text-ink-3">· {hl.section_matches}×</span>
            )}
          </span>
          <span
            className={`text-ink ${markClass}`}
            {...jaTextProps(hl.source)}
            dangerouslySetInnerHTML={{ __html: renderHeadline(hl.headline) }}
          />
        </button>
      ))}

      {total > 0 && (
        <a
          href={keywordDrugHref(result, result.highlights[0] ?? null, searchKeywords, exactPhrase, undefined, tabId || undefined)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => openInAppLink(e, keywordDrugHref(result, result.highlights[0] ?? null, searchKeywords, exactPhrase, undefined, tabId || undefined))}
          className="mt-2 inline-block text-[13px] text-accent hover:text-accent-deep hover:underline"
        >
          Show all {total} matches in the label
        </a>
      )}
    </div>
  );
};

// ─── Collapsible Result Group ────────────────────────────────────────────────

interface ResultGroupProps {
  label: string;
  count: number;
  defaultExpanded: boolean;
  children: React.ReactNode;
}

const ResultGroup: React.FC<ResultGroupProps> = ({ label, count, defaultExpanded, children }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div className="border border-line rounded-ctl overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-panel hover:bg-line-subtle transition-colors"
      >
        <div className="flex items-center gap-2">
          <svg
            className={`w-4 h-4 text-ink-3 transition-transform ${expanded ? 'rotate-90' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-sm font-semibold text-ink">{label}</span>
        </div>
        <span className="px-2 py-0.5 text-xs font-medium bg-line text-ink-2 rounded-full">
          {count} {count === 1 ? 'drug' : 'drugs'}
        </span>
      </button>
      {expanded && (
        <div className="p-3 space-y-3 bg-white">
          {children}
        </div>
      )}
    </div>
  );
};

// ─── Group-By Dropdown ───────────────────────────────────────────────────────



interface GroupByDropdownProps {
  value: GroupByOption;
  onChange: (value: GroupByOption) => void;
}

const GROUP_OPTIONS: { value: GroupByOption; label: string; short: string }[] = [
  { value: 'none', label: 'No Grouping', short: 'none' },
  { value: 'generic', label: 'By Generic Name', short: 'generic' },
  { value: 'manufacturer', label: 'By Manufacturer', short: 'manufacturer' },
  { value: 'type', label: 'By Application Type', short: 'application type' },
  { value: 'region', label: 'By Region', short: 'region' },
];

const GroupByDropdown: React.FC<GroupByDropdownProps> = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const activeShort = GROUP_OPTIONS.find((o) => o.value === value)?.short ?? 'none';

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`btn-secondary h-9 text-[13px] ${value !== 'none' ? 'border-accent bg-accent-tint text-accent-deep' : ''}`}
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
        Group: {activeShort}
        <svg className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-44 bg-white rounded-ctl shadow-lg border border-line py-1 z-30">
          {GROUP_OPTIONS.map((option) => (
            <button
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className={`w-full px-3 py-2 text-left text-sm transition-colors ${
                value === option.value
                  ? 'text-accent-deep bg-accent-tint font-medium'
                  : 'text-ink hover:bg-panel'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const noopSort = () => {};

// ─── Results Container ───────────────────────────────────────────────────────

interface KeywordSearchResultsProps {
  /** US-Drugs@FDA visible: US reads "US-DailyMed" (group labels, Region column). */
  usFdaVisible?: boolean;
  response: KeywordSearchResponse | null;
  isLoading: boolean;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  searchKeywords?: string;
  /** One entry per row: `[keyword, ...that row's synonyms]`. When more than
   *  one distinct row keyword is present, excerpts are colored per row (with
   *  each row's synonyms added in that row's color) and a color legend is
   *  shown — matching compare/export. A single row never recolors, so the
   *  backend's word-level and stem-variant marks (which already cover that
   *  row's synonyms) survive untouched. */
  searchTerms?: string[][];
  /** When true, the click-through highlights the contiguous phrase (one
   *  <mark>) instead of splitting into per-word highlights. */
  exactPhrase?: boolean;
  /** The current keyword search request — passed down from SearchPage so
   *  SubscribeSearchButton can subscribe to the active search. Null when no
   *  search has been run yet. */
  kwRequest?: KeywordSearchRequest | null;
  /** Near on-demand: the server-side sort the rows arrive in (null = default order). */
  serverSort?: DrugSort | null;
  onServerSortChange?: (next: DrugSort) => void;
  /** Near on-demand: passages could not be produced (409/503/partial drop);
   *  the surface refetches the current page. */
  onPassagesUnavailable?: () => void;
  /** Changes when the results are replaced by a new search/page/sort; clears the row ticks. */
  selectionResetKey?: string;
}

/** Export for an on-demand page: fetches the page's passages (one request, rows
 *  in displayed order) before any export action; a failed fetch aborts the export
 *  and asks the surface to refetch the page. */
const OnDemandExport: React.FC<{
  rows: KeywordSearchResultItem[];
  request: KeywordSearchRequest | null;
  coloredTerms?: ColoredTerm[];
  keyword?: string;
  onUnavailable?: () => void;
}> = ({ rows, request, coloredTerms, keyword, onUnavailable }) => {
  const queryClient = useQueryClient();
  const loadResults = useCallback(async (): Promise<KeywordSearchResultItem[]> => {
    if (!request) throw new Error('no keyword request');
    try {
      const passages = await queryClient.fetchQuery(
        keywordPassagesQueryOptions(request, rows.map((r) => r.drug_id)),
      );
      return mergePassages(rows, passages);
    } catch (err) {
      if (isPassagesUnavailable(err)) onUnavailable?.();
      throw err;
    }
  }, [queryClient, request, rows, onUnavailable]);
  return (
    <ExportDropdown
      results={rows}
      loadResults={loadResults}
      {...(coloredTerms ? { coloredTerms } : {})}
      {...(keyword ? { keyword } : {})}
    />
  );
};

/** Expanded panel for an on-demand row: fetches that one drug's passages. */
const OnDemandMatches: React.FC<{
  row: KeywordSearchResultItem;
  request: KeywordSearchRequest;
  searchKeywords?: string;
  exactPhrase?: boolean;
  coloredTerms: ColoredTerm[];
  onLoaded: (merged: KeywordSearchResultItem) => void;
  onUnavailable?: () => void;
}> = ({ row, request, searchKeywords, exactPhrase, coloredTerms, onLoaded, onUnavailable }) => {
  const { data, error, isLoading } = useQuery({
    ...keywordPassagesQueryOptions(request, [row.drug_id]),
    staleTime: KW_PASSAGES_STALE_MS,
  });
  const merged = useMemo(() => (data ? mergePassages([row], data)[0] ?? null : null), [data, row]);
  useEffect(() => {
    if (merged) onLoaded(merged);
  }, [merged, onLoaded]);
  const unavailable = error != null && isPassagesUnavailable(error);
  const notified = useRef(false);
  useEffect(() => {
    if (unavailable && !notified.current) {
      notified.current = true;
      onUnavailable?.();
    }
  }, [unavailable, onUnavailable]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-3 text-sm text-ink-3">
        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        Loading matches…
      </div>
    );
  }
  if (unavailable) return <p className="py-3 text-sm text-ink-3">Refreshing results…</p>;
  if (error) return <p className="py-3 text-sm text-red-600">Could not load matches.</p>;
  if (!merged) return null;
  if (merged.highlights.length === 0) {
    return <p className="py-3 text-sm text-ink-3">No matching passages in the current label text.</p>;
  }
  return (
    <KeywordMatchesPanel
      result={merged}
      {...(searchKeywords ? { searchKeywords } : {})}
      {...(exactPhrase ? { exactPhrase } : {})}
      {...(coloredTerms.length > 1 ? { coloredTerms } : {})}
    />
  );
};

// Count-forward UX: boolean AND/OR is CROSS-SECTION (terms match anywhere in the
// label), so common terms legitimately match many drugs. Above this true-total we
// nudge the user to narrow. Only shown for exact (non-windowed) totals.
const BROAD_RESULT_THRESHOLD = 1000;
const EMPTY_PASSAGES: Record<number, KeywordSearchResultItem> = {};
// Results-page tick cap; deliberately below CompareKeywordPage MAX_PANELS (100) — keep 5 (spec 2026-10-15 §7.2).
const MAX_COMPARE_PANELS = 5;
// Mirrors the server's MAX_RANK_SORT_DRUGS: above this, Matches ranking is unavailable.
const MAX_RANK_SORT_DRUGS = 5000;

const KeywordSearchResults: React.FC<KeywordSearchResultsProps> = ({
  response,
  isLoading,
  currentPage,
  pageSize,
  onPageChange,
  searchKeywords,
  searchTerms,
  exactPhrase,
  kwRequest = null,
  serverSort = null,
  onServerSortChange,
  onPassagesUnavailable,
  selectionResetKey,
  usFdaVisible = false,
}) => {
  const onDemand = response?.passages_on_demand === true;
  // Japanese-text search opens the Japanese viewer even without a passage.
  const jpJaCorpus = kwRequest?.sources?.includes('jp_ja') ? 'jp_ja' : null;
  // Passages learned by expanding rows (drug_id -> merged row). Tied to the
  // response they were fetched for: a new response starts empty.
  const [passagesState, setPassagesState] = useState<{
    response: KeywordSearchResponse | null;
    byId: Record<number, KeywordSearchResultItem>;
  }>({ response: null, byId: {} });
  const passagesById = passagesState.response === response ? passagesState.byId : EMPTY_PASSAGES;
  const recordPassages = useCallback((merged: KeywordSearchResultItem) => {
    setPassagesState((prev) => {
      const byId = prev.response === response ? prev.byId : {};
      if (byId[merged.drug_id] === merged) return prev;
      return { response, byId: { ...byId, [merged.drug_id]: merged } };
    });
  }, [response]);
  // Chips + grouping are mirrored into the URL (kwRefine / kwGroup) so Back
  // from Compare, a reload or a shared link restores the same filtered view.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlGroup = searchParams.get(KW_GROUP_PARAM);
  const urlRefine = searchParams.get(KW_REFINE_PARAM);
  const [groupBy, setGroupByState] = useState<GroupByOption>(() => parseGroupParam(urlGroup));
  const [sort, setSort] = useState<DrugSort | null>(null);
  const [chips, setChipsState] = useState<RefineChip[]>(() => parseRefineParam(urlRefine));
  const navigate = useNavigate();
  // Reverse-sync: a URL change that does NOT remount this table (a Recent
  // Search replay, browser Back/Forward between two searches) must move the
  // chips/grouping too — otherwise the table and the URL disagree, and Back
  // from Compare restores whatever the URL says rather than what was shown.
  // Our own writes come back here unchanged and are no-ops.
  useEffect(() => {
    const nextChips = parseRefineParam(urlRefine);
    setChipsState((prev) => (serializeRefineChips(prev) === serializeRefineChips(nextChips) ? prev : nextChips));
    const nextGroup = parseGroupParam(urlGroup);
    setGroupByState((prev) => (prev === nextGroup ? prev : nextGroup));
  }, [urlRefine, urlGroup]);
  const writeViewParam = useCallback((key: string, value: string) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (value) params.set(key, value);
      else params.delete(key);
      return params;
    }, { replace: true });
  }, [setSearchParams]);
  const setChips = useCallback((next: RefineChip[]) => {
    setChipsState(next);
    writeViewParam(KW_REFINE_PARAM, serializeRefineChips(next));
  }, [writeViewParam]);
  const setGroupBy = useCallback((next: GroupByOption) => {
    setGroupByState(next);
    writeViewParam(KW_GROUP_PARAM, next === 'none' ? '' : next);
  }, [writeViewParam]);
  // Approval dates are reliable for every region (2026-10-15 backfill); the
  // column is always shown.
  const showApprovalDate = true;
  // One row's worth of terms never recolors (the backend's marks already
  // cover that row's synonyms); recoloring kicks in only once there is more
  // than one distinct row keyword, matching today's row-count behavior.
  const coloredTerms = useMemo(() => {
    const rows = searchTerms ?? [];
    const rowTerms = buildColoredTerms(rows.map((r) => r[0]));
    if (rowTerms.length <= 1) return rowTerms;
    const seen = new Set(rowTerms.map((c) => c.term.toLowerCase()));
    const extra: ColoredTerm[] = [];
    for (const r of rows) {
      const color = rowTerms.find((c) => c.term.toLowerCase() === (r[0] ?? '').trim().toLowerCase())?.color;
      if (!color) continue;
      for (const s of r.slice(1)) {
        const t = s.trim();
        if (!t || seen.has(t.toLowerCase())) continue;
        seen.add(t.toLowerCase());
        extra.push({ term: t, color }); // synonym in its row's color
      }
    }
    return [...rowTerms, ...extra];
  }, [searchTerms]);

  // Filter before sort/group (spec §4.2a), both over a COPY of response.results
  // — never mutated, so Export/Compare keep reading the backend order.
  // baseRows = every loaded row on this page: unlike Cogni, the chip bar is
  // the only filter here, so there is no "other filters" population to
  // exclude the chip bar's own filter from.
  const baseRows = useMemo(
    () => (response?.results ?? []).map((r) => drugTableRefineFields(r, showApprovalDate)),
    [response, showApprovalDate],
  );
  const filtered = useMemo(
    () => (response?.results ?? []).filter(
      (r) => matchesRefineChips(drugTableRefineFields(r, showApprovalDate), chips),
    ),
    [response, chips, showApprovalDate],
  );

  const groupedResults = useMemo(() => {
    if (!response) return null;
    // Shared with the compare view's rail (utils/groupResults) so a grouping
    // chosen here means the same thing after the jump to Compare.
    const groups = groupRows(filtered, groupBy, usFdaVisible);
    return groups ? groups.map((g) => [g.label, g.rows] as [string, typeof filtered]) : null;
  }, [response, filtered, groupBy, usFdaVisible]);

  // On-demand Export keeps today's row set (every loaded row, not narrowed by
  // Refine chips) but follows the DISPLAYED order, which groupRows may reorder.
  const exportRows = useMemo(() => {
    const all = response?.results ?? [];
    if (!onDemand || groupBy === 'none') return all;
    return groupRows(all, groupBy, usFdaVisible)?.flatMap((g) => g.rows) ?? all;
  }, [response, onDemand, groupBy, usFdaVisible]);

  // Rows ticked for Compare, in tick order. Cleared when the surface changes
  // `selectionResetKey` (new search, page or sort), kept across plain rerenders.
  const [selected, setSelected] = useState<number[]>([]);
  const [compareError, setCompareError] = useState(false);
  // Read without throwing: only the on-demand Compare hydration needs the client,
  // and inline-mode mounts need no provider.
  const queryClient = useContext(QueryClientContext);
  const { storageKey, tabId } = useTab();
  // Bumped on reset and unmount: a Compare still hydrating passages must not
  // write or navigate once the selection it was started for is gone.
  const compareGen = useRef(0);
  useEffect(() => {
    compareGen.current += 1;
    setSelected([]);
    setCompareError(false);
  }, [selectionResetKey]);
  useEffect(() => () => { compareGen.current += 1; }, []);
  const selectedKeys = useMemo(() => new Set<React.Key>(selected), [selected]);
  const toggleSelect = useCallback((row: KeywordSearchResultItem) => {
    setSelected((prev) => {
      if (prev.includes(row.drug_id)) return prev.filter((id) => id !== row.drug_id);
      return prev.length < MAX_COMPARE_PANELS ? [...prev, row.drug_id] : prev;
    });
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex items-center gap-3 text-ink-3">
          <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="text-sm">Searching labels...</span>
        </div>
      </div>
    );
  }

  if (!response) return null;

  // R7/AC7: "N+" / top-matches is keyed on the response, not request shape —
  // any capped/windowed executor (legacy Exact stream, Near, boolean-FTS
  // estimate) sets this, not just a single-Exact-row request.
  const approximateTotal = response.count_is_windowed === true;

  if (response.results.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-panel rounded-full mb-4">
          <svg className="w-8 h-8 text-ink-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <NativeQueryNote nq={response.native_query} />
        <p className="text-lg text-ink-2">No results found</p>
        {response.warnings && response.warnings.length > 0 ? (
          <div className="mt-3 max-w-md mx-auto">
            {response.warnings.map((w, i) => (
              <p key={i} className="text-sm text-warn-ink bg-warn-bg border border-warn-line rounded-ctl px-3 py-2 mt-1">
                {w}
              </p>
            ))}
          </div>
        ) : (
          <p className="text-sm text-ink-3 mt-1">
            Try different keywords, broaden your search, or adjust filters
          </p>
        )}
      </div>
    );
  }

  const filterActive = chips.length > 0;
  const quotedTerms = (searchTerms ?? []).map((r) => r[0]).filter(Boolean).map((t) => `"${t}"`).join(', ');
  const matchWords = quotedTerms
    ? `match ${quotedTerms}${approximateTotal ? ' (top matches)' : ''}`
    : (approximateTotal ? '(top matches)' : '');

  const openCompare = async () => {
    setCompareError(false);
    const gen = compareGen.current;
    let seedRows: KeywordSearchResultItem[] = [];
    if (selected.length > 0) {
      const byId = new Map(response.results.map((r) => [r.drug_id, r]));
      const picked = selected.map((id) => byId.get(id)).filter((r): r is KeywordSearchResultItem => !!r);
      seedRows = picked.map((r) => (onDemand ? (passagesById[r.drug_id] ?? r) : r));
      const missing = onDemand && kwRequest && queryClient ? picked.filter((r) => !passagesById[r.drug_id]) : [];
      if (missing.length > 0 && kwRequest && queryClient) {
        try {
          const passages = await queryClient.fetchQuery(
            keywordPassagesQueryOptions(kwRequest, missing.map((r) => r.drug_id)),
          );
          const merged = new Map(mergePassages(missing, passages).map((r) => [r.drug_id, r]));
          seedRows = seedRows.map((r) => merged.get(r.drug_id) ?? r);
        } catch (err) {
          if (gen !== compareGen.current) return;
          if (isPassagesUnavailable(err)) onPassagesUnavailable?.();
          else setCompareError(true);
          return;
        }
      }
      if (gen !== compareGen.current) return;
      if (kwRequest) {
        try {
          // Compare reads the rail's request and scopes the seed to it, so a
          // search restored from the URL/Back hands both over together.
          sessionStorage.setItem(storageKey('kw-request'), JSON.stringify(kwRequest));
          sessionStorage.setItem(
            storageKey('kw-compare-seed'),
            JSON.stringify({ search: kwSearchIdentity(kwRequest), rows: seedRows }),
          );
        } catch {
          setCompareError(true);
          return;
        }
      }
    } else {
      try {
        if (kwRequest) sessionStorage.setItem(storageKey('kw-request'), JSON.stringify(kwRequest));
        sessionStorage.removeItem(storageKey('kw-compare-seed'));
      } catch { /* Compare only looks up rail rows without panels */ }
    }
    // Carry the active grouping and Refine chips into Compare: the rail groups
    // and filters the same way, so it lists the drugs this table was narrowed to.
    navigate(compareKeywordPath({
      kw: searchKeywords ?? '',
      group: groupBy !== 'none' ? groupBy : '',
      refine: serializeRefineChips(chips),
      sort: onDemand && serverSort ? serializeSortParam(serverSort) : '',
      panels: selected,
    }));
  };

  const renderTable = (rows: KeywordSearchResultItem[]) => (
    <DrugResultsTable
      usFdaVisible={usFdaVisible}
      rows={rows}
      sort={onDemand ? serverSort : sort}
      onSortChange={onDemand ? (onServerSortChange ?? noopSort) : setSort}
      rowKey={(r) => r.drug_id}
      getBrandHref={(r) => {
        const m = onDemand ? (passagesById[r.drug_id] ?? r) : r;
        return keywordDrugHref(m, m.highlights[0] ?? null, searchKeywords, exactPhrase, jpJaCorpus, tabId || undefined);
      }}
      {...(onDemand && kwRequest && queryClient ? {
        // A Near row has no passages until expanded: fetch this drug's, then
        // open the document its first match is in (not the drug's default).
        onBrandClick: (r: KeywordSearchResultItem, e: React.MouseEvent) => {
          // Outside the shell a window.open after the fetch can be popup-blocked:
          // keep the plain drug link there.
          if (passagesById[r.drug_id] || !hasShellOpener() || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
            openInAppLink(e, keywordDrugHref(passagesById[r.drug_id] ?? r, (passagesById[r.drug_id] ?? r).highlights[0] ?? null, searchKeywords, exactPhrase, jpJaCorpus, tabId || undefined));
            return;
          }
          e.preventDefault();
          void keywordBrandHref(r, async (row) => {
            const p = await queryClient.fetchQuery(keywordPassagesQueryOptions(kwRequest, [row.drug_id]));
            const merged = mergePassages([row], p)[0];
            if (merged) recordPassages(merged);
            return merged;
          }, { ...(searchKeywords ? { keyword: searchKeywords } : {}), ...(exactPhrase ? { exactPhrase } : {}), corpus: jpJaCorpus, ...(tabId ? { tabId } : {}) })
            .then(openInNewTab);
        },
      } : {})}
      showApprovalDate={showApprovalDate}
      showMatches
      selectedKeys={selectedKeys}
      onToggleSelect={toggleSelect}
      selectionFull={selected.length >= MAX_COMPARE_PANELS}
      {...(onDemand ? {
        presorted: true,
        renderMatches: (r: KeywordSearchResultItem) =>
          passagesById[r.drug_id]?.total_matches ?? (response.rank_sort === 'match_count' ? r.total_matches : '—'),
      } : {})}
      canExpand={onDemand ? () => kwRequest !== null : (r) => r.highlights.length > 0}
      renderExpanded={onDemand ? (r) => (kwRequest ? (
        <OnDemandMatches
          row={r}
          request={kwRequest}
          {...(searchKeywords ? { searchKeywords } : {})}
          {...(exactPhrase ? { exactPhrase } : {})}
          coloredTerms={coloredTerms}
          onLoaded={recordPassages}
          {...(onPassagesUnavailable ? { onUnavailable: onPassagesUnavailable } : {})}
        />
      ) : null) : (r) => (
        <KeywordMatchesPanel
          result={r}
          {...(searchKeywords ? { searchKeywords } : {})}
          {...(exactPhrase ? { exactPhrase } : {})}
          {...(coloredTerms.length > 1 ? { coloredTerms } : {})}
        />
      )}
    />
  );

  return (
    <div>
      <NativeQueryNote nq={response.native_query} />
      {/* Warnings banner (e.g. stop words) */}
      {response.warnings && response.warnings.length > 0 && (
        <div className="mb-3">
          {response.warnings.map((w, i) => (
            <p key={i} className="text-sm text-warn-ink bg-warn-bg border border-warn-line rounded-ctl px-3 py-2 mt-1">
              {w}
            </p>
          ))}
        </div>
      )}

      {/* Results header */}
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-3">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          {response.total_count > 0 ? (
            <>
              <h2 className="font-display text-[22px] font-medium text-ink">
                <span>{response.total_count.toLocaleString()}{approximateTotal ? '+' : ''}</span>{' '}
                {response.total_count === 1 ? 'drug' : 'drugs'}
              </h2>
              <span className="text-[13px] text-ink-2">
                {[
                  matchWords,
                  `${(response.latency_ms / 1000).toFixed(1)} s`,
                  `showing ${((currentPage - 1) * pageSize) + 1}–${Math.min(currentPage * pageSize, response.total_count)}`,
                ].filter(Boolean).join(' · ')}
              </span>
            </>
          ) : (
            <>
              <h2 className="font-display text-[22px] font-medium text-ink">
                {response.total_drugs} {response.total_drugs === 1 ? 'drug' : 'drugs'} found
              </h2>
              <span className="text-[13px] text-ink-2">{(response.latency_ms / 1000).toFixed(1)} s</span>
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Compare results. With nothing ticked it opens the default 2-panel
              view (top 2); ticked rows become the panels. */}
          {(response.results.length >= 2 || selected.length > 0) && (
            <button
              data-testid="kw-compare"
              onClick={openCompare}
              className="btn-primary h-9"
              title="Compare results (tick rows to choose the drugs)"
            >
              {selected.length > 0 ? `Compare ${selected.length}` : 'Compare'}
            </button>
          )}
          {compareError && (
            <span role="alert" className="text-[13px] text-signal">
              Couldn't open Compare with your selection. Try again.
            </span>
          )}

          {/* Group by dropdown */}
          <GroupByDropdown value={groupBy} onChange={setGroupBy} />

          {/* Export dropdown — always the backend order, every loaded row,
              unaffected by the display sort/filter. */}
          {onDemand ? (
            <OnDemandExport
              rows={exportRows}
              request={kwRequest}
              {...(onPassagesUnavailable ? { onUnavailable: onPassagesUnavailable } : {})}
              {...(coloredTerms.length > 1 ? { coloredTerms } : {})}
              {...(searchKeywords ? { keyword: searchKeywords } : {})}
            />
          ) : (
            <ExportDropdown
              results={response.results}
              {...(coloredTerms.length > 1 ? { coloredTerms } : {})}
              {...(searchKeywords ? { keyword: searchKeywords } : {})}
            />
          )}

          {/* Subscribe to search results */}
          <SubscribeSearchButton kwRequest={kwRequest} />
        </div>
      </div>

      {/* Synonyms used indicator */}
      {response.synonyms_used && Object.keys(response.synonyms_used).length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-accent-deep font-medium">Synonyms expanded:</span>
          {Object.entries(response.synonyms_used).map(([keyword, syns]) => (
            <span
              key={keyword}
              className="px-2 py-0.5 text-xs bg-accent-tint text-accent-deep rounded-full"
              title={syns.join(', ')}
            >
              {keyword} +{syns.length}
            </span>
          ))}
        </div>
      )}

      {/* Count-forward: broad cross-section match hint. AND/OR match terms anywhere
          in the label (cross-section), so common terms can match many drugs — nudge
          the user to narrow. Skipped when the total is windowed. */}
      {!approximateTotal && response.total_count > BROAD_RESULT_THRESHOLD && (
        <div className="mb-3 flex items-start gap-2 rounded-ctl border border-accent-ring bg-accent-tint px-3 py-2 text-xs text-accent-deep">
          <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>
            <strong>{response.total_count.toLocaleString()} drugs</strong> mention your terms anywhere in the label.
            Add another keyword or scope a term to a section to narrow the results.
            {onDemand && response.rank_sort === 'skipped_too_large' && (
              <> Ranking by matches is unavailable for more than {MAX_RANK_SORT_DRUGS.toLocaleString()} drugs,
              so these results are sorted by brand name.</>
            )}
            {!onDemand && response.rank_sort === 'skipped_too_large' && (
              <> These results are <strong>not ranked by relevance</strong> — there are too many
              matches to order them, so they appear in database order.</>
            )}
          </span>
        </div>
      )}

      {/* On-demand order notice (D5): only while no server sort is active. */}
      {onDemand && serverSort === null && (
        <p className="mb-3 text-xs text-ink-2">
          {response.total_count > MAX_RANK_SORT_DRUGS
            ? `Ranking by matches is unavailable for more than ${MAX_RANK_SORT_DRUGS.toLocaleString()} drugs — sorted by brand name.`
            : 'Sorted by brand name — click Matches to rank by number of matches.'}
        </p>
      )}

      {/* Multi-row query color legend (one swatch per term: each row keyword, plus its synonyms in that row's color). */}
      {coloredTerms.length > 1 && (
        <div className="mb-3 rounded-ctl border border-line-subtle bg-panel px-3 py-2">
          <HighlightLegend terms={coloredTerms} />
        </div>
      )}

      {/* Filter box (spec §4.2a) */}
      <ResultsFilterBox
        chips={chips}
        onChipsChange={setChips}
        baseRows={baseRows}
        shownCount={filtered.length}
        totalCount={response.results.length}
        usFdaVisible={usFdaVisible}
        showPageNote={onDemand
          ? response.total_count > pageSize && filterActive
          : response.total_count > pageSize && (sort !== null || filterActive)}
      />

      {/* Result rows — flat or grouped, filtered then sorted (display only). */}
      {filterActive && filtered.length === 0 ? (
        <FilterNoMatches onClear={() => setChips([])} />
      ) : groupBy === 'none' || !groupedResults ? (
        renderTable(filtered)
      ) : (
        <div className="space-y-3">
          {groupedResults.map(([label, results], index) => (
            <ResultGroup key={label} label={label} count={results.length} defaultExpanded={index < 3}>
              {renderTable(results)}
            </ResultGroup>
          ))}
        </div>
      )}

      {/* Pagination bar */}
      <ResultsPagination
        currentPage={currentPage}
        totalCount={response.total_count}
        pageSize={pageSize}
        onPageChange={onPageChange}
      />
    </div>
  );
};

export default KeywordSearchResults;
